import { isTeamManagerTarget } from 'components/usersTable/helper';
import { MESSAGE_MAX_LENGTH } from 'constants/invites';
import { ACC_OWNER } from 'constants/roles';
import { UserTeam } from 'contexts/teams/types';
import { UserInvites } from 'contexts/users/types';
import { findAssignedSkillDive } from 'hooks/useSkillDiveToggleState';
import { Invite } from 'pages/Users/components/InviteUsers/AddByEmails/invitesReducer';

export const INVALID_DOMAIN_ERROR =
  "Failed: The email address doesn't match with account valid domains";

export const userFullName = (user: Pick<UserInvites, 'uaa_data'>): string => {
  const profile = user.uaa_data?.profile?.data;
  return `${profile?.first_name ?? ''} ${profile?.last_name ?? ''}`.trim();
};

export const userEmail = (user: UserInvites): string => user.uaa_data?.email ?? user.email;

// An existing user as an access-step row, starting from their current access.
export const toExistingInvite = (user: UserInvites, teamEntry: UserTeam): Invite => {
  const hasLicense = Boolean(user.license);
  const licenseAddons = user.license_addons ?? [];
  return {
    id: user.id,
    email: userEmail(user),
    name: userFullName(user) || undefined,
    avatar: user.uaa_data?.profile?.data?.avatar,
    is_account_owner: user.uaa_data
      ? Boolean(user.uaa_data.roles?.data?.some((role) => role.name === ACC_OWNER))
      : user.role === ACC_OWNER,
    is_manager: false,
    has_license: hasLicense,
    has_skill_dive: Boolean(findAssignedSkillDive({ license_addons: licenseAddons })),
    userTeams: [{ ...teamEntry }],
    existing: {
      user: user.user,
      uaa_id: user.uaa_id,
      is_team_manager: isTeamManagerTarget(user),
      has_license: hasLicense,
      license_addons: licenseAddons,
      teams: (user.teams ?? []).map((userTeam) => ({
        team_id: userTeam.team_id || userTeam.team?.id,
        is_manager: Boolean(userTeam.is_manager),
      })),
    },
  };
};

// The default invite message. The message box can't shorten text it starts with, so a long
// name is cut here to fit the limit.
export const defaultInviteMessage = (inviterName: string, teamName: string): string => {
  const message = (
    inviterName
      ? `${inviterName} has invited you to join ${teamName} on INE Business.`
      : `You have been invited to join ${teamName} on INE Business.`
  ).slice(0, MESSAGE_MAX_LENGTH);
  // Don't leave half of a two-unit character, such as an emoji, at the cut.
  return /[\uD800-\uDBFF]$/.test(message) ? message.slice(0, -1) : message;
};
